import { useState, type FormEvent } from 'react'
import { api, storeToken } from '../lib/api.ts'
import { FOCUS_RING } from '../components/focusRing.ts'

/**
 * Email, then code. Two steps because that is what the flow is.
 *
 * Markup and copy are V1's, token for token: a pharmacist moving between the
 * two systems should not be able to tell which one they are looking at.
 *
 * The screen never says whether an address is known. That would make this page
 * a way to find out who works at a pharmacy, and the server already refuses to
 * tell us -- the interface must not invent the distinction back.
 */
export function Login({ onSignedIn }: { onSignedIn: (email: string) => void }) {
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>()

  async function submitEmail(event: FormEvent) {
    event.preventDefault()
    if (email.trim() === '') return
    setBusy(true)
    setError(undefined)
    try {
      await api.requestCode(email.trim())
      setStep('code')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'não foi possível enviar o código')
    } finally {
      setBusy(false)
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault()
    if (code.trim() === '') return
    setBusy(true)
    setError(undefined)
    try {
      const { token, user } = await api.verifyCode(email.trim(), code.trim())
      storeToken(token)
      onSignedIn(user.email)
    } catch {
      // Deliberately not the server's message: a wrong code and an unknown
      // address must look identical from here.
      setError('código inválido ou expirado')
    } finally {
      setBusy(false)
    }
  }

  const field = `w-full rounded-lg border px-3 py-2 text-sm transition-colors bg-background-raised text-text-strong ${FOCUS_RING}`
  const submit = `w-full rounded-lg px-4 py-2 text-sm font-semibold transition-opacity disabled:opacity-50 bg-fill-brand text-text-inverse-strong ${FOCUS_RING}`

  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="bg-background-raised w-full max-w-[400px] rounded-xl border p-8">
        <h1 className="text-text-strong mb-6 text-xl font-semibold">Entrar</h1>

        {step === 'email' ? (
          <form onSubmit={submitEmail} noValidate>
            <div className="mb-6">
              <label htmlFor="email" className="text-text-weak mb-1 block text-sm font-medium">
                E-mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={field}
                disabled={busy}
              />
            </div>

            {error !== undefined && (
              <p
                role="alert"
                className="text-text-error mb-4 rounded-lg bg-[rgba(239,68,68,0.08)] px-3 py-2 text-sm"
              >
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className={submit}>
              {busy ? 'Enviando…' : 'Enviar código'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode} noValidate>
            <p className="text-text-weak mb-4 text-sm">
              Enviamos um código de acesso para {email}.
            </p>

            <div className="mb-6">
              <label htmlFor="code" className="text-text-weak mb-1 block text-sm font-medium">
                Código de acesso
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={field}
                disabled={busy}
              />
            </div>

            {error !== undefined && (
              <p
                role="alert"
                className="text-text-error mb-4 rounded-lg bg-[rgba(239,68,68,0.08)] px-3 py-2 text-sm"
              >
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className={submit}>
              {busy ? 'Entrando…' : 'Entrar'}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep('email')
                setError(undefined)
              }}
              className={`text-text-weak mt-3 w-full rounded-lg text-center text-sm ${FOCUS_RING}`}
            >
              Usar outro e-mail
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
